import { forwardRef, memo, type Ref, type MouseEventHandler } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// Material Icon imports
import AddBoxIcon from '@mui/icons-material/AddBox';
// SCSS imports
import { button, icon } from './PlusButton.module.scss';

type PlusButtonProps = {
  readonly label: string;
  readonly disabled?: boolean;
  readonly pending?: boolean;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly link?: string;
  readonly sx?: ButtonProps['sx'];
};

const PlusButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, PlusButtonProps>(({
  label,
  disabled = false,
  pending = false,
  onClick,
  link,
  sx
}, ref) => link ? (
  <Button
      ref={ref as Ref<HTMLAnchorElement>}
      sx={sx}
      color="secondary"
      disabled={disabled || pending}
      className={button}
      startIcon={pending ? (
        <CircularProgress color="inherit" size="2.4rem"/>
      ) : (
        <AddBoxIcon
            className={icon}
            color={disabled ? undefined : 'primary'}
        />
      )}
      component={RouterLink}
      to={link}
  >
    <FormattedMessage id={label} defaultMessage={label}/>
  </Button>
) : (
  <Button
      ref={ref as Ref<HTMLButtonElement>}
      sx={sx}
      color="secondary"
      disabled={disabled || pending}
      className={button}
      startIcon={pending ? (
        <CircularProgress color="inherit" size="2.4rem"/>
      ) : (
        <AddBoxIcon
            className={icon}
            color={disabled ? undefined : 'primary'}
        />
      )}
      onClick={onClick}
  >
    <FormattedMessage id={label} defaultMessage={label}/>
  </Button>
));

PlusButton.displayName = 'PlusButton';

export default memo(PlusButton);
